import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiBell, FiCheck, FiCheckCircle, FiTrash2 } from 'react-icons/fi';
import { notificationsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { connectSocket, disconnectSocket } from '../../services/socket';

const canViewAllNotificationsRole = (role) => {
  const r = String(role || '').toLowerCase();
  return r === 'super_admin' || r === 'admin';
};

const NotificationList = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [viewAllUsers, setViewAllUsers] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const filterRef = useRef('all');
  const notifFeedRef = useRef({ myUid: null, allowOthers: false });

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    notifFeedRef.current = {
      myUid: user?.user_id != null ? Number(user.user_id) : null,
      allowOthers: canViewAllNotificationsRole(user?.role) && viewAllUsers
    };
  }, [user?.user_id, user?.role, viewAllUsers]);

  const loadNotifications = useCallback(async ({ reset, pageOverride } = { reset: false, pageOverride: null }) => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'unread') params.is_read = 0;
      if (filter === 'read') params.is_read = 1;
      params.limit = 20;
      const effectivePage = reset ? 1 : (pageOverride || page);
      params.page = effectivePage;
      if (canViewAllNotificationsRole(user?.role) && viewAllUsers) params.scope = 'all';
      const res = await notificationsAPI.getAll(params);
      const items = res.data.notifications || res.data || [];
      setNotifications((prev) => (reset ? items : [...prev, ...items]));
      const total = Number(res.data.total ?? NaN);
      if (Number.isFinite(total)) {
        const loaded = reset ? items.length : ((effectivePage - 1) * 20 + items.length);
        setHasMore(loaded < total);
      } else {
        setHasMore(items.length >= 20);
      }
      if (reset) setPage(1);
    } catch (err) { toast.error(t('notifications.failedLoad')); }
    setLoading(false);
  }, [filter, page, user?.role, viewAllUsers]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadNotifications({ reset: true });
  }, [filter, viewAllUsers, user?.user_id]);

  useEffect(() => {
    const socket = connectSocket();

    const onNew = (payload) => {
      if (!payload) return;
      const { myUid, allowOthers } = notifFeedRef.current;
      const targetUid = payload.user_id != null ? Number(payload.user_id) : null;
      if (targetUid == null) return;
      if (myUid != null && targetUid !== myUid && !allowOthers) return;
      const f = filterRef.current;
      if (f === 'unread' && payload.is_read) return;
      if (f === 'read' && !payload.is_read) return;

      setNotifications((prev) => {
        const id = payload.notification_id || payload.notif_id;
        const filtered = prev.filter((n) => (n.notification_id || n.notif_id) !== id);
        return [payload, ...filtered];
      });
    };

    const onMarkAll = () => {
      setNotifications((prev) => {
        const f = filterRef.current;
        if (f === 'unread') return [];
        return prev.map((n) => ({ ...n, is_read: 1 }));
      });
    };

    socket.on('notification:new', onNew);
    socket.on('notification:mark_all_read', onMarkAll);

    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:mark_all_read', onMarkAll);
      disconnectSocket();
    };
  }, []);

  const handleMarkRead = async (notifId) => {
    try {
      await notificationsAPI.markRead(notifId);
      loadNotifications({ reset: true });
    } catch (err) { toast.error(t('common.failed')); }
  };

  const handleMarkAllRead = async () => {
    try {
      const allowAll = canViewAllNotificationsRole(user?.role) && viewAllUsers;
      await notificationsAPI.markAllRead(allowAll ? { scope: 'all' } : undefined);
      toast.success(t('notifications.markedAllRead'));
      setFilter('all');
      setPage(1);
      setHasMore(true);
      loadNotifications({ reset: true, pageOverride: 1 });
    } catch (err) { toast.error(t('common.failed')); }
  };

  const handleDelete = async (notifId) => {
    try {
      await notificationsAPI.delete(notifId);
      loadNotifications({ reset: true });
    } catch (err) { toast.error(t('notifications.deleteFailed')); }
  };

  const getTypeIcon = (type) => {
    const icons = {
      'lead_assigned': '\uD83D\uDC64', 'lead_converted': '\uD83D\uDD04', 'meeting_reminder': '\uD83D\uDCC5',
      'followup_due': '\u23F0', 'document_uploaded': '\uD83D\uDCC4', 'application_update': '\uD83D\uDCCB',
      'system': '\u2699\uFE0F'
    };
    return icons[type] || '\uD83D\uDD14';
  };

  const getTypeBg = (type) => {
    const colors = {
      'lead_assigned': 'var(--info-light)', 'lead_converted': 'var(--success-light)',
      'meeting_reminder': 'var(--warning-light)', 'followup_due': 'var(--danger-light)',
      'document_uploaded': 'var(--purple-light)', 'application_update': 'var(--teal-light)',
      'system': 'var(--bg-secondary)'
    };
    return colors[type] || 'var(--bg-secondary)';
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiBell style={{ marginRight: 8 }} />{t('notifications.title')}</h2>
          <p className="page-subtitle">{t('notifications.unreadCount', { count: unreadCount })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handleMarkAllRead}>
            <FiCheckCircle /> {t('notifications.markAllRead')}
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="tabs" style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', gap: 0 }}>
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>{t('notifications.tabAll')}</button>
          <button className={filter === 'unread' ? 'active' : ''} onClick={() => setFilter('unread')}>
            {t('notifications.tabUnread')} {unreadCount > 0 && <span style={{
              background: 'var(--danger)', color: 'var(--white)', marginLeft: 4,
              padding: '1px 6px', borderRadius: 10, fontSize: 11, fontWeight: 700
            }}>{unreadCount}</span>}
          </button>
          <button className={filter === 'read' ? 'active' : ''} onClick={() => setFilter('read')}>{t('notifications.tabRead')}</button>
        </div>
        {canViewAllNotificationsRole(user?.role) && (
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              userSelect: 'none', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600
            }}
            title={t('notifications.allUsersHint')}
          >
            <input
              type="checkbox"
              checked={viewAllUsers}
              onChange={(e) => setViewAllUsers(e.target.checked)}
              style={{ width: 15, height: 15, margin: 0, cursor: 'pointer' }}
            />
            {t('notifications.allUsers')}
          </label>
        )}
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div>
          {notifications.map(n => (
            <div key={n.notif_id}
              style={{
                display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 4,
                background: n.is_read ? 'var(--white)' : 'var(--primary-50)', borderRadius: 'var(--radius-lg)',
                border: `1px solid ${n.is_read ? 'var(--border)' : 'var(--primary-lighter)'}`,
                cursor: 'pointer', transition: 'var(--transition)'
              }}
              onClick={() => !n.is_read && handleMarkRead(n.notif_id)}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', background: getTypeBg(n.type),
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0
              }}>
                {getTypeIcon(n.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <strong style={{ fontSize: 14, fontWeight: n.is_read ? 400 : 600, color: 'var(--text)' }}>
                    {n.message?.substring(0, 60)}
                  </strong>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {!n.is_read && (
                      <button onClick={(e) => { e.stopPropagation(); handleMarkRead(n.notif_id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 4 }} title={t('notifications.markAsRead')}>
                        <FiCheck size={14} />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(n.notif_id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} title={t('common.delete')}>
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
                <p style={{ margin: '4px 0', fontSize: 13, color: 'var(--text-secondary)' }}>{n.message}</p>
                {canViewAllNotificationsRole(user?.role) && viewAllUsers && (n.target_user_name || n.created_by_name) && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    {n.target_user_name ? <span>{t('notifications.for')}<strong>{n.target_user_name}</strong></span> : null}
                    {n.created_by_name ? <span>{n.target_user_name ? ' · ' : ''}{t('notifications.by')}<strong>{n.created_by_name}</strong></span> : null}
                  </div>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {new Date(n.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </span>
              </div>
              {!n.is_read && <div style={{
                width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)',
                flexShrink: 0, marginTop: 6
              }}></div>}
            </div>
          ))}
          {notifications.length === 0 && (
            <div className="empty-state">
              <FiBell size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p>{t('notifications.noNotifications')}</p>
            </div>
          )}
          {notifications.length > 0 && hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={loadingMore}
                onClick={async () => {
                  setLoadingMore(true);
                  try {
                    const next = page + 1;
                    setPage(next);
                    await loadNotifications({ reset: false, pageOverride: next });
                  } finally {
                    setLoadingMore(false);
                  }
                }}
              >
                {loadingMore ? t('common.loading') : t('header.loadMore')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
